import { type AnyAction } from 'redux';
import { call, CallEffect, ForkEffect, put, PutEffect, takeLatest, delay } from 'redux-saga/effects';

import { SORT_COLUMN_KEYS } from '@containers/leads/allLeads/constants';
import { SORT_QUOTATION_COLUMN_KEYS } from '@containers/leads/quotationTool/constants';
import { handleEmptyNullValues, handleSortingValues, mergeQuotationProductForUpdate, normalizeWarrantyCodes, restoreCleanedQuotationProduct, stripQuotationProductUiFields, toQuotationStringField, transformUploadData, uploadDocumentArr } from '@utilities';
import { endOfDay, startOfDay } from 'date-fns';
import { activateDocument } from '../products/api';
import { autocompleteUser } from '../user/api';
import {
	addAreaToQuotation,
	addLeadQuotation,
	autocompleteLead,
	changeAreaNickname,
	changeQuotationImage,
	copyQuotationItem,
	createLead,
	deleteQuotationVersion,
	fetchFCBuilderData,
	fetchLeadQuotation,
	fetchQuotationToolList,
	getLeadDetails,
	getLeadProjects,
	getLeads,
	sendUploadQuotation,
	updateFCBuilder,
	updateLeadDetails,
	updateLeadQuotation,
	uploadDocument,
	fetchLeadCredits,
	addLeadCredits,
	importQuotation,
	getLeadSourceHistory,
	getLeadRemarks,
	putLeadRemark,
	startRefreshQuotationCostingAsync,
	getRefreshQuotationCostingJob,
	getQuotationCostingSyncStatus
} from './api';
import { LeadsActionType } from './enums';
import { sqftToMm } from '@components/QuotationList/utils';
type LeadsSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type LeadsSagaForEffect = Generator<ForkEffect<void>>;

const leadsFiltersObject = (obj: Record<string, any>) => {
	const pickFirstOrSelf = (value: any) => (Array.isArray(value) ? value[0] : value);
	const rawSource = pickFirstOrSelf(obj.filters?.source);
	const sourceValue =
		rawSource && typeof rawSource === 'object' ? rawSource.value || rawSource.key || null : rawSource || null;
	const selectedLead = pickFirstOrSelf(obj.filters?.leadName);
	const selectedAddedBy = pickFirstOrSelf(obj.filters?.addedBy);
	const transformData = {
		filters: {
			...obj.filters,
			status: !!obj.filters.leadStatus?.length ? obj.filters.leadStatus.map(status => status.value) : [],
			subStatus: !!obj.filters.subStatus?.length ? obj.filters.subStatus.map(status => status.value) : [],
			updatedOnStart: !!obj.filters.updatedOn?.length ? startOfDay(obj.filters?.updatedOn[0]).getTime() : null,
			updatedOnEnd: !!obj.filters.updatedOn?.length ? endOfDay(obj.filters?.updatedOn[1]).getTime() : null,
			addedOnStart: !!obj.filters.addedOn?.length ? startOfDay(obj.filters?.addedOn[0]).getTime() : null,
			addedOnEnd: !!obj.filters.addedOn?.length ? endOfDay(obj.filters?.addedOn[1]).getTime() : null,
			leadId: selectedLead?.leadId || selectedLead?.id || '',
			source: sourceValue,
			addedBy: selectedAddedBy?.userId || selectedAddedBy?.user_id || selectedAddedBy?.id || ''
		}
	};
	delete transformData?.filters?.leadName;
	delete transformData?.filters?.leadStatus;
	delete transformData?.filters?.updatedOn;
	delete transformData?.filters?.addedOn;
	return {
		...obj,
		filters: transformData.filters
	};
};

export function* fetchLeads(action: AnyAction): LeadsSagaEffect {
	const { leadsFilters, errorCB } = action.payload;
	let transformedFilters = handleSortingValues(leadsFilters, SORT_COLUMN_KEYS);
	const transformedData = leadsFiltersObject(transformedFilters);
	transformedFilters = handleEmptyNullValues(transformedData);

	try {
		const { data }: any = yield call(getLeads, handleEmptyNullValues(transformedFilters));
		const leadsList = data.leads;
		yield put({ type: LeadsActionType.STORE_LEADS, payload: { leadsList, totalRecords: data.totalRecords } });
	} catch (error: unknown) {
		yield put({
			type: LeadsActionType.ERROR_LEADS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchLeadDetailsEffect(action: AnyAction): LeadsSagaEffect {
	const { leadID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getLeadDetails, leadID);
		yield put({ type: LeadsActionType.STORE_LEAD_DETAILS, payload: { data } });
	} catch (error: unknown) {
		errorCB(error?.message);
		yield put({
			type: LeadsActionType.ERROR_STORE_LEAD_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* fetchLeadProjectsEffect(action: AnyAction): LeadsSagaEffect {
	const { leadID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getLeadProjects, leadID);
		yield put({ type: LeadsActionType.STORE_LEAD_PROJECTS, payload: { data } });
	} catch (error: unknown) {
		errorCB(error?.message);
		yield put({
			type: LeadsActionType.ERROR_FETCH_LEAD_PROJECTS,
			payload: {
				error
			}
		});
	}
}

export function* updateLeadBasicDetailsEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, leadID, successCB, errorCB } = action.payload;
	const transformData = {
		altPhoneNumber: formData.altPhoneNumber ? formData.altPhoneNumber : null,
		email: formData.email ? formData.email : null,
		fullName: formData.fullName ? formData.fullName : null,
		phoneNumber: formData.phoneNumber ? formData.phoneNumber : null,
		source: formData.source ? formData.source : null,
		status: formData.status ? formData.status : null,
		subStatus: formData.subStatus ? formData.subStatus : null
	};
	try {
		yield call(updateLeadDetails, transformData, leadID);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
		yield put({
			type: LeadsActionType.ERROR_STORE_LEAD_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* createLeadEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, successCB, errorCB } = action.payload;
	const leadDetails = handleEmptyNullValues(formData);
	try {
		const { data }: any = yield call(createLead, leadDetails);
		successCB(data);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchLeadAutocompleteEffect(action: AnyAction): LeadsSagaEffect {
	const { prefix, errorCB } = action.payload;

	try {
		const { data }: any = yield call(autocompleteLead, prefix);
		const projectsList = data?.leads?.map((projectItem: Record<string, any>) => {
			return {
				...projectItem,
				displayName: `${projectItem.fullName} (${projectItem.phoneNumber})${projectItem.projectName ? ' - ' + projectItem.projectName : ''}`
			};
		});

		yield put({ type: LeadsActionType.STORE_LEAD_AUTOCOMPLETE, payload: { data: projectsList } });
	} catch (error: unknown) {
		yield put({
			type: LeadsActionType.ERROR_FETCH_LEAD_AUTOCOMPLETE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchLeadUserAutocompleteEffect(action: AnyAction): LeadsSagaEffect {
	const { prefix, errorCB } = action.payload;

	try {
		const { data }: any = yield call(autocompleteUser, prefix);
		const userList = data?.users?.map((user: Record<string, any>) => {
			return {
				...user,
				displayName: `${user.fullName} (${user.phoneNumber})`
			};
		});

		yield put({ type: LeadsActionType.STORE_LEAD_USER_AUTOCOMPLETE, payload: { data: userList } });
	} catch (error: unknown) {
		yield put({
			type: LeadsActionType.ERROR_FETCH_LEAD_USER_AUTOCOMPLETE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchLeadQuotationEffect(action: AnyAction): LeadsSagaEffect {
	const { leadID, errorCB, projectID } = action.payload;

	try {
		const { data }: any = yield call(fetchLeadQuotation, { leadID, projectID });
		yield put({ type: LeadsActionType.STORE_LEAD_QUOTATIONS, payload: { data: data.quotations } });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchQuotationToolListEffect(action: AnyAction): LeadsSagaEffect {
	const { errorCB, quotationID } = action.payload;

	try {
		const { data }: any = yield call(fetchQuotationToolList, quotationID);
		yield put({ type: LeadsActionType.STORE_QUOTATION_LIST, payload: { data: data } });
		yield put({
			type: LeadsActionType.FETCH_COSTING_SYNC_STATUS,
			payload: { quotationId: quotationID }
		});
	} catch (error: unknown) {
		yield put({
			type: LeadsActionType.ERROR_QUOTATION_LIST,
			payload: {
				error
			}
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchQuotationToolListBackgroundEffect(action: AnyAction): LeadsSagaEffect {
	const { quotationID } = action.payload;

	try {
		const { data }: any = yield call(fetchQuotationToolList, quotationID);
		yield put({ type: LeadsActionType.STORE_QUOTATION_LIST_BACKGROUND, payload: { data: data } });
	} catch (error: unknown) {
		yield put({
			type: LeadsActionType.ERROR_QUOTATION_LIST_BACKGROUND,
			payload: {
				error
			}
		});
		// Silent error, maybe log to console
		console.error('Background fetch failed:', error);
	}
}

export function* importQuotationEffect(action: AnyAction): LeadsSagaEffect {
	const { quotationId, payload, successCB, errorCB, finalCB } = action.payload || {};
	try {
		yield call(importQuotation, quotationId, payload);
		if (successCB) successCB();
	} catch (error: unknown) {
		if (errorCB) errorCB(error?.message);
	} finally {
		if (finalCB) finalCB();
	}
}

export function* updateQuotationItemEffect(action: AnyAction): LeadsSagaEffect {
	const { values, successCB, errorCB } = action.payload;

	try {
		// yield call(updateQuotationItem, formData, leadID);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* uploadQuotationPDFEffect(action: AnyAction): LeadsSagaEffect {
	const { errorCB, successCB, formData } = action.payload;
	try {
		const { data }: any = yield call(sendUploadQuotation, formData);

		const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [data]);
		yield call(async () => await Promise.all(arrPromises));

		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}
export function* deleteQuotationVersionEffect(action: AnyAction): LeadsSagaEffect {
	const { itemId, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteQuotationVersion, itemId);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

function getQuotationProductMapKey(product: Record<string, any>) {
	return `${product?.productId}-${product?.areaEnum}-${product?.floor ?? 0}`;
}

function buildQuotationProductMap(quotationData: any) {
	const productMap = new Map<string, any>();
	if (!quotationData?.areas) return productMap;

	quotationData.areas.forEach((area: Record<string, any>) => {
		(area.products || []).forEach((product: Record<string, any>) => {
			productMap.set(getQuotationProductMapKey(product), product);
		});
	});

	return productMap;
}

function transformQuotationData(data, existingProductsMap: Map<string, any> = new Map()) {
	const {
		finalAddedProducts,
		addedProducts,
		fcBuilderProductId,
		updatedProducts: updatedProductsData,
		...rest
	} = data;

	return {
		...rest,
		updatedProducts: Array.isArray(updatedProductsData)
			? updatedProductsData.map((product: Record<string, any>) =>
					mergeQuotationProductForUpdate(
						existingProductsMap.get(getQuotationProductMapKey(product)),
						product
					)
				)
			: [],
		addedProducts: Array.isArray(finalAddedProducts)
			? finalAddedProducts.map(product => {
					const linkedVariantId = String(
						product?.parametricVariantId ||
							product?.parametric_variant_id ||
							product?.variantId ||
							product?.variant_id ||
							''
					).trim();
					return stripQuotationProductUiFields({
						...product,
						areaEnum: product?.area?.areaEnum || product?.areaEnum || '',
						areaName: product?.area?.areaName || product?.areaName || '',
						floor: product?.floor || 0,
						productId: product?.productId,
						productName: product?.productName || '',
						unitOfMeasure: product?.unitOfMeasure || '',
						price: product?.total || product?.price || 0,
						unitCost: product?.price || product?.unitCost || 0,
						specification: product?.specification || '',
						description: product?.description || '',
						calculation: toQuotationStringField(product?.calculation),
						measurement: {
							...(product?.measurement || {}),
							width: product?.measurement?.width || 0,
							depth: product?.measurement?.depth || 0,
							height: product?.measurement?.height || 0,
							area: sqftToMm(product?.measurement?.area) || product?.measurement?.area || 0
						},
						quantity: product?.quantity || 0,
						productOrder: product?.productOrder ?? null,
						total: product?.total || 0,
						type: product?.type || '',
						productSubCategoryName: toQuotationStringField(
							product?.productSubCategoryName,
							'',
							['subCategoryName']
						),
						productCategoryName: toQuotationStringField(
							product?.productCategoryName,
							'',
							['categoryName']
						),
						imageUrl: product?.imageUrl || '',
						materialCode: product?.materialCode || '',
						builder: product?.builder || '',
						paintSurfaces: product?.paintSurfaces || [],
						warrantyCodes: normalizeWarrantyCodes(product?.warrantyCodes),
						remarks: product?.remarks || '',
						...(linkedVariantId
							? {
									variantType: 'PARAMETRIC',
									variantId: linkedVariantId,
									parametricVariantId: linkedVariantId,
									...(product?.variantName || product?.variant_name
										? { variantName: product.variantName || product.variant_name }
										: {}),
									...(product?.variantVersion != null || product?.variant_version != null
										? {
												variantVersion:
													product.variantVersion ?? product.variant_version
											}
										: {})
								}
							: {})
					});
				})
			: []
	};
}

export function* updateLeadQuotationEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, successCB, errorCB, refreshOnDescopeErrorCB } = action.payload;
	// Send the UI total as a snapshot; the backend recalculates persisted totals from active lines.
	// Do not re-derive from server payable totals — that diverged from "Gross total" in the UI.
	const grandTotal = Math.round(Number(formData.grandTotal) || 0);

	try {
		const quotationID = formData.quotationID;
		const updatedProducts = formData.updatedProducts || [];
		let existingProductsMap = new Map<string, any>();
		const isReorderOnly =
			updatedProducts.length > 0 && updatedProducts.every((p: Record<string, any>) => p.isReorder);

		if (quotationID && updatedProducts.length > 0) {
			try {
				const { data: quotationData }: any = yield call(fetchQuotationToolList, quotationID);
				existingProductsMap = buildQuotationProductMap(quotationData);
			} catch (fetchError: any) {
				console.warn('Failed to fetch quotation for update payload enrichment:', fetchError?.message);
			}
		}

		// Extract productId from updatedProducts (if any) before transforming
		const updatedProductsList = formData.updatedProducts || [];
		let productId = null;
		if (updatedProductsList && Array.isArray(updatedProductsList) && updatedProductsList.length > 0) {
			// Get productId from the first updated product
			const firstUpdatedProduct = updatedProductsList[0];
			productId =
				firstUpdatedProduct?.productId ||
				firstUpdatedProduct?.itemId?.itemId ||
				firstUpdatedProduct?.itemId ||
				null;
		}

		// Transform the data (updatedProducts will not be included in the result)
		const transformedData = transformQuotationData(
			{
				...formData,
				grandTotal
			},
			existingProductsMap
		);

		const preCleanUpdatedProducts = transformedData.updatedProducts || [];
		const preCleanAddedProducts = transformedData.addedProducts || [];

		// Clean empty values
		const transformedReqData = handleEmptyNullValues(transformedData);
		// Ensure grandTotal is whole rupees (matches UI formatCurrencyINR(..., 0, 'round') + backend roundForResponse)
		transformedReqData.grandTotal = grandTotal;

		if (transformedReqData.updatedProducts) {
			transformedReqData.updatedProducts = transformedReqData.updatedProducts.map(
				(product: Record<string, any>, idx: number) =>
					restoreCleanedQuotationProduct(product, preCleanUpdatedProducts[idx] || product)
			);
		}

		if (transformedReqData.addedProducts) {
			transformedReqData.addedProducts = transformedReqData.addedProducts.map(
				(product: Record<string, any>, idx: number) =>
					restoreCleanedQuotationProduct(product, preCleanAddedProducts[idx] || product)
			);
		}

		// Add productId at root level if it exists
		if (productId) {
			transformedReqData.productId = productId;
		}

		// Reorder is arrangement/view only — always patch in place, never version via copyFrom.
		if (isReorderOnly) {
			const reorderQuotationId =
				transformedReqData.quotationID ||
				transformedReqData.quotationId ||
				formData.quotationID ||
				formData.copyFromQuotationId;
			if (reorderQuotationId) {
				transformedReqData.quotationID = reorderQuotationId;
			}
			delete transformedReqData.copyFromQuotationId;
			delete transformedReqData.copyFromQuotationID;
		}

		yield call(updateLeadQuotation, transformedReqData);
		successCB();
	} catch (error: unknown) {
		if (error?.status === 423 && refreshOnDescopeErrorCB) {
			refreshOnDescopeErrorCB();
		} else {
			errorCB(error?.message);
		}
	}
}

export function* addAreaToQuotationEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, finalCB, errorCB } = action.payload;
	try {
		yield call(addAreaToQuotation, formData.quotationID, { areaEnum: formData.area, floor: formData.floor });
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* changeAreaNicknameEffect(action: AnyAction): LeadsSagaEffect {
	const { quotationID, areaName, nickname, floor, successCB, errorCB } = action.payload;
	try {
		yield call(changeAreaNickname, quotationID, { areaName, nickname, floor });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* uploadQuotationProductImageEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, successCB, errorCB } = action.payload;
	const currentFileType = 'MEDIA';
	const revisedTransformedObj = transformUploadData(formData.document, currentFileType);
	const updateImageDocRequest = {
		referenceId: revisedTransformedObj.document[0].referenceId,
		referenceType: revisedTransformedObj.document[0].referenceType,
		documents: revisedTransformedObj.document.map((doc: Record<string, any>) => ({
			category: doc.category,
			subCategory: doc.subCategory,
			documentId: doc.documentId,
			attachments: revisedTransformedObj.document[0].files.map((file: Record<string, any>) => ({
				file: file.fileName
			}))
		}))
	};

	try {
		const { data }: any = yield call(uploadDocument, updateImageDocRequest);
		const { documentsResponseList } = data;
		const { attachments, category, subCategory, documentId } = documentsResponseList[0];
		const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [
			{ category, subCategory, attachments, documentId }
		]);
		yield call(async () => await Promise.all(arrPromises));
		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}
		successCB(uploadedDocuments);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* changeQuotationImageEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, successCB, errorCB } = action.payload;
	const { quotationID, productId, areaEnum, areaName, documentId, floor } = formData;
	try {
		yield call(changeQuotationImage, {
			quotationId: quotationID,
			floor,
			productId,
			areaEnum,
			areaName,
			documentId
		});
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* copyQuotationItemEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, quotationID, successCB, errorCB } = action.payload;
	try {
		yield call(copyQuotationItem, quotationID, formData);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}
export function* updateFCBuilderEffect(action: AnyAction): LeadsSagaEffect {
	const { formData, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(updateFCBuilder, handleEmptyNullValues(formData));
		successCB(data?.totalCost, data?.length, data?.breadth, formData.productId, data);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchFCBuilderDataEffect(action: AnyAction): LeadsSagaEffect {
	const { productId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchFCBuilderData, productId);

		yield put({ type: LeadsActionType.STORE_BUILDER_FC_DATA, payload: data });
	} catch (error: unknown) {
		yield put({ type: LeadsActionType.STORE_BUILDER_FC_DATA, payload: null });
		errorCB(error?.message);
	}
}

export function* fetchLeadCreditsEffect(action: AnyAction): LeadsSagaEffect {
	const { userId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchLeadCredits, userId);
		yield put({ type: LeadsActionType.STORE_LEAD_CREDITS, payload: data?.creditLimit - data?.creditsUsed || 0 });
	} catch (error: unknown) {
		yield put({ type: LeadsActionType.ERROR_LEAD_CREDITS });
		errorCB(error?.message);
	}
}

export function* addLeadCreditsEffect(action: AnyAction): LeadsSagaEffect {
	const { userId, credits, successCB, errorCB } = action.payload;
	try {
		yield call(addLeadCredits, userId, credits);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* refreshQuotationCostingEffect(action: AnyAction): LeadsSagaEffect {
	const { quotationId, successCB, errorCB } = action.payload || {};

	try {
		const { data: startData }: any = yield call(startRefreshQuotationCostingAsync, quotationId);
		const jobId = startData?.job_id || startData?.jobId;

		if (!jobId) {
			throw new Error('Refresh job ID not returned');
		}

		let attempt = 0;
		const maxAttempts = 300;

		while (attempt < maxAttempts) {
			const pollDelayMs = attempt < 10 ? 5000 : 7000;
			yield delay(pollDelayMs);
			attempt += 1;

			const { data: jobData }: any = yield call(getRefreshQuotationCostingJob, jobId);
			const status = jobData?.status;

			if (status === 'SUCCEEDED') {
				yield put({
					type: LeadsActionType.STORE_REFRESH_QUOTATION_COSTING_STATUS,
					payload: {
						status: 'SUCCEEDED',
						result: jobData?.result || null
					}
				});
				const refreshedQuotationId =
					jobData?.result?.quotation_id || jobData?.result?.quotationId || quotationId;
				yield put({
					type: LeadsActionType.FETCH_COSTING_SYNC_STATUS,
					payload: { quotationId: refreshedQuotationId }
				});
				if (successCB) successCB(jobData?.result || null);
				return;
			}

			if (status === 'FAILED') {
				const errorMessage = jobData?.error || 'Refresh quotation costing failed';
				yield put({
					type: LeadsActionType.REFRESH_QUOTATION_COSTING_FAILED,
					payload: { error: errorMessage }
				});
				if (errorCB) errorCB(errorMessage);
				return;
			}
		}

		throw new Error('Refresh quotation costing timed out. Please try again.');
	} catch (error: any) {
		const errorMessage =
			error?.response?.status === 404
				? 'Refresh job not found or expired. Please retry.'
				: error?.message || 'Failed to refresh quotation costing';

		yield put({
			type: LeadsActionType.REFRESH_QUOTATION_COSTING_FAILED,
			payload: { error: errorMessage }
		});

		if (errorCB) errorCB(errorMessage);
	}
}

export function* fetchCostingSyncStatusEffect(action: AnyAction): LeadsSagaEffect {
	const { quotationId } = action.payload || {};
	if (!quotationId) {
		return;
	}

	try {
		const { data }: any = yield call(getQuotationCostingSyncStatus, quotationId);
		yield put({
			type: LeadsActionType.STORE_COSTING_SYNC_STATUS,
			payload: {
				needsRecalculate: Boolean(data?.needs_recalculate ?? data?.needsRecalculate),
				driftCount: data?.drift_count ?? data?.driftCount ?? 0,
				skippedCount: data?.skipped_count ?? data?.skippedCount ?? 0
			}
		});
	} catch (error: any) {
		yield put({
			type: LeadsActionType.ERROR_COSTING_SYNC_STATUS,
			payload: { error: error?.message || 'Failed to fetch costing sync status' }
		});
	}
}

export function* fetchLeadSourceHistoryEffect(action: AnyAction): LeadsSagaEffect {
	const { leadID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getLeadSourceHistory, leadID);
		yield put({ type: LeadsActionType.STORE_LEAD_SOURCE_HISTORY, payload: { data } });
	} catch (error: unknown) {
		errorCB(error?.message);
		yield put({
			type: LeadsActionType.ERROR_FETCH_LEAD_SOURCE_HISTORY,
			payload: {
				error
			}
		});
	}
}

export function* fetchLeadRemarksEffect(action: AnyAction): LeadsSagaEffect {
	const { leadID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getLeadRemarks, leadID);
		const list = data?.data ?? data ?? [];
		const remarks = Array.isArray(list) ? list : [];
		yield put({ type: LeadsActionType.STORE_LEAD_REMARKS, payload: { data: remarks } });
	} catch (error: unknown) {
		yield put({
			type: LeadsActionType.ERROR_FETCH_LEAD_REMARKS,
			payload: { error }
		});
		errorCB?.(error?.message);
	}
}

export function* putLeadRemarkEffect(action: AnyAction): LeadsSagaEffect {
	const { leadID, remark, successCB, errorCB } = action.payload;
	try {
		yield call(putLeadRemark, leadID, { remark });
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	}
}

// Sagas

export function* fetchLeadsSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEADS, fetchLeads);
}

export function* createLeadSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.CREATE_LEAD, createLeadEffect);
}

export function* fetchLeadDetailsSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_DETAILS, fetchLeadDetailsEffect);
}

export function* updateLeadBasicDetailsSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.UPDATE_LEAD_BASIC_DETAILS, updateLeadBasicDetailsEffect);
}

export function* fetchLeadProjectsSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_PROJECTS, fetchLeadProjectsEffect);
}

export function* fetchLeadAutocompleteSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_AUTOCOMPLETE, fetchLeadAutocompleteEffect);
	yield takeLatest(LeadsActionType.FETCH_LEAD_USER_AUTOCOMPLETE, fetchLeadUserAutocompleteEffect);
}

export function* fetchLeadQuotationSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_QUOTATIONS, fetchLeadQuotationEffect);
}

export function* fetchQuotationToolListSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_QUOTATION_LIST, fetchQuotationToolListEffect);
	yield takeLatest(LeadsActionType.FETCH_QUOTATION_LIST_BACKGROUND, fetchQuotationToolListBackgroundEffect);
}

export function* updateQuotationItemSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.UPDATE_QUOTATION_ITEM, updateQuotationItemEffect);
}

export function* uploadQuotationPDFSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.UPLOAD_QUOTATION_PDF, uploadQuotationPDFEffect);
}

export function* deleteQuotationVersionSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.DELETE_QUOTATION_VERSION, deleteQuotationVersionEffect);
}

export function* updateLeadQuotationSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.UPDATE_LEAD_QUOTATION, updateLeadQuotationEffect);
}

export function* addAreaToQuotationQuotationSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.ADD_AREA_TO_QUOTATION, addAreaToQuotationEffect);
}

export function* changeAreaNicknameSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.CHANGE_AREA_NICKNAME, changeAreaNicknameEffect);
}

export function* uploadQuotationImageSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.UPLOAD_QUOTATION_IMAGE, uploadQuotationProductImageEffect);
}

export function* changeQuotationImageSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.CHANGE_QUOTATION_IMAGE, changeQuotationImageEffect);
}

export function* copyQuotationItemSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.COPY_QUOTATION_ITEM, copyQuotationItemEffect);
}

export function* updateFCBuilderSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.UPDATE_FC_BUILDER, updateFCBuilderEffect);
}

export function* fetchFCBuilderDataSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_BUILDER_FC_DATA, fetchFCBuilderDataEffect);
}

export function* fetchLeadCreditsSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_CREDITS, fetchLeadCreditsEffect);
}

export function* addLeadCreditsSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.ADD_LEAD_CREDITS, addLeadCreditsEffect);
}

export function* importQuotationSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.IMPORT_QUOTATION, importQuotationEffect);
}

export function* fetchLeadSourceHistorySaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_SOURCE_HISTORY, fetchLeadSourceHistoryEffect);
}

export function* fetchLeadRemarksSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_LEAD_REMARKS, fetchLeadRemarksEffect);
}

export function* putLeadRemarkSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.PUT_LEAD_REMARK, putLeadRemarkEffect);
}

export function* refreshLeadQuotationCostingSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.REFRESH_QUOTATION_COSTING, refreshQuotationCostingEffect);
}

export function* fetchCostingSyncStatusSaga(): LeadsSagaForEffect {
	yield takeLatest(LeadsActionType.FETCH_COSTING_SYNC_STATUS, fetchCostingSyncStatusEffect);
}
